// Interface para a resposta do upload de CSV de frota
export interface FleetUploadResponse {
  success: boolean;
  message: string;
  processedRecords?: number;
  failedRecords?: number;
  errors?: string[];
  // Erros por linha retornados com 200 OK quando há críticas de processamento
  processingErrors?: Record<string, string[]>;
}

// Interface para validação de chassis
export interface ChassisValidation {
  [key: string]: string;
}

// Interface para resultado de validação
export interface ValidationResultItem {
  status: 'Sucesso' | 'Erro';
  fileName: string;
  message: string;
  chassis: ChassisValidation;
}

// Interface para resposta de erro de validação
export interface ValidationErrorResponse {
  message: string;
  validationResult: ValidationResultItem[];
}

// Interface para erros do serviço
export interface FleetServiceError {
  message: string;
  code?: string;
  status?: number;
}

// API URL
const API_URL = process.env.REACT_APP_API_URL;

/**
 * Serviço para gerenciamento de frotas
 */
export const fleetService = {
  /**
   * Upload de arquivo CSV para cadastro de frota
   * @param file - Arquivo CSV a ser enviado
   * @returns Promise com a resposta do upload
   */
  uploadFleetCSV: async (file: File): Promise<FleetUploadResponse> => {
    try {
      // Verificar se é um arquivo CSV
      if (!file.name.toLowerCase().endsWith('.csv')) {
        throw new Error('Apenas arquivos CSV são aceitos');
      }

      // Criar FormData para envio do arquivo
      const formData = new FormData();
      formData.append('file', file);

      // Obter token de autenticação do localStorage
      const token = localStorage.getItem('authToken') || '';

      // Enviar requisição para a API
      const response = await fetch(`${API_URL}/api/v1/fleet/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      // Verificar se houve erro na requisição
      if (!response.ok) {
        const errorData = await response.json();
        
        // Se for erro de validação (status 422)
        if (response.status === 422 && errorData.validationResult) {
          const validationError = new Error(errorData.message);
          (validationError as any).status = 422;
          (validationError as any).validationError = true;
          (validationError as any).validationResult = errorData.validationResult;
          throw validationError;
        }
        
        throw new Error(errorData.message || 'Erro ao fazer upload do arquivo');
      }

      // Retornar dados de sucesso
      const data = await response.json();

      const processingErrors: Record<string, string[]> | undefined =
        data.erros && Object.keys(data.erros).length > 0 ? data.erros : undefined;

      return {
        success: !processingErrors,
        message: data['message:'] || data.message,
        processedRecords: data.processedRecords,
        failedRecords: data.failedRecords,
        processingErrors
      };
    } catch (error) {
      console.error('Erro no upload de frota:', error);
      if (error instanceof Error) {
        throw error;
      } else {
        throw new Error('Não foi possível processar o arquivo. Por favor, tente novamente.');
      }
    }
  }
};
